// The revenue-cycle ledger behind the RCM teaser and /concepts/revenue-cycle:
// what is reasonably at stake for one provider platform, every line a public
// benchmark × the platform's scale. Pure and node-tested; the figure on the
// concept page runs it live and keeps its inputs in the URL (?encounters=…)
// so a NoVA tour step or a mailed link carries a scenario. The arithmetic is
// the one printed in docs/teaser/onboarding-teaser-rcm*.html.
//
// Benchmarks (sources on the page): initial denial rate 11.8% avg, <4% top
// decile (ABA 2026, Kodiak 2025); $43.84 labor to rework a denied claim
// (Premier 2024); cost to collect 9.4% avg vs 3–4% top decile and days in A/R
// 42 vs 18 (ABA 2026); ~half of denials originate in registration/eligibility.

export const INPUT_SPEC = [
  { key: 'encounters', label: 'Encounters a year', unit: '', range: [50000, 20000000, 50000], group: 'The platform' },
  { key: 'netPerVisit', label: 'Net revenue per visit', unit: '$', range: [40, 8000, 5], group: 'The platform' },
  { key: 'denialRate', label: 'Initial denial rate', unit: '%', range: [2, 30, 0.1], group: 'Where it is today' },
  { key: 'reworkCost', label: 'Labor to rework one denial', unit: '$', range: [10, 150, 1], group: 'Where it is today' },
  { key: 'costToCollect', label: 'Cost to collect', unit: '%', range: [2, 20, 0.1], group: 'Where it is today' },
  { key: 'arDays', label: 'Days in A/R', unit: 'days', range: [10, 120, 1], group: 'Where it is today' },
  { key: 'targetDenial', label: 'Denial rate after the program', unit: '%', range: [1, 30, 0.1], group: 'The program' },
  { key: 'topDecileCtc', label: 'Top-decile cost to collect', unit: '%', range: [1, 10, 0.1], group: 'The program' },
  { key: 'arTarget', label: 'Days in A/R after', unit: 'days', range: [5, 120, 1], group: 'The program' },
  { key: 'rampMonths', label: 'Months to run-rate', unit: 'mo', range: [0, 24, 1], group: 'The program' },
  { key: 'payerShare', label: 'Top-3 payers’ share of denials', unit: '%', range: [10, 100, 1], group: 'The first initiative' },
  { key: 'frontEndShare', label: 'Denials that start at registration', unit: '%', range: [10, 90, 1], group: 'The first initiative' },
  { key: 'prevented', label: 'Of those, prevented at intake', unit: '%', range: [10, 100, 1], group: 'The first initiative' },
];

// The urgent-care platform shape from the Revelstoke teaser: ~2.2M encounters
// at $175 net a visit (≈ $385M), benchmark averages everywhere else.
export const DEFAULTS = {
  encounters: 2200000, netPerVisit: 175,
  denialRate: 11.8, reworkCost: 44, costToCollect: 9.4, arDays: 42,
  targetDenial: 8, topDecileCtc: 4, arTarget: 36, rampMonths: 6,
  payerShare: 40, frontEndShare: 50, prevented: 60,
};

const clamp = (v, [lo, hi]) => Math.min(hi, Math.max(lo, v));

export function normalize(input = {}) {
  const out = { ...DEFAULTS };
  for (const s of INPUT_SPEC) {
    const v = Number(input[s.key]);
    if (Number.isFinite(v)) out[s.key] = clamp(v, s.range);
  }
  return out;
}

export function runLedger(input = {}) {
  const i = normalize(input);
  const npr = i.encounters * i.netPerVisit;
  const denials = i.encounters * (i.denialRate / 100);
  const reworkSpend = denials * i.reworkCost;
  const fewerShare = Math.max(0, (i.denialRate - i.targetDenial) / i.denialRate); // 32% at the defaults
  const reworkSaved = denials * fewerShare * i.reworkCost;
  const ctcSpend = npr * (i.costToCollect / 100);
  const ctcHeadroom = Math.max(0, npr * ((i.costToCollect - i.topDecileCtc) / 100));
  const ctcPoint = npr / 100;
  const perDay = npr / 365;
  const arCash = Math.max(0, (i.arDays - i.arTarget) * perDay);
  const firstShare = (i.payerShare / 100) * (i.frontEndShare / 100) * (i.prevented / 100); // 12% at the defaults
  const firstYear = denials * firstShare * i.reworkCost;
  const first36 = firstYear * 3;
  const runRateYears = Math.max(0, (36 - i.rampMonths) / 12);
  const total36 = reworkSaved * runRateYears + arCash;
  const pct = (usd) => (npr ? usd / npr : 0);
  return {
    inputs: i,
    npr,
    denials,
    first: { usd: firstYear, pct: pct(firstYear), fewerShare: firstShare, by36: first36 },
    rework: { spend: reworkSpend, usd: reworkSaved, pct: pct(reworkSaved), fewerShare, firstIsShareOf: reworkSaved ? firstYear / reworkSaved : 0 },
    ar: { perDay, days: Math.max(0, i.arDays - i.arTarget), usd: arCash, pct: pct(arCash) },
    ctc: { spend: ctcSpend, headroom: ctcHeadroom, pct: pct(ctcHeadroom), point: ctcPoint },
    total: { usd: total36, pctOf3yr: npr ? total36 / (3 * npr) : 0, runRateYears },
  };
}

// URL state: only keys that differ from the defaults are written; unknown
// keys and other params pass through untouched (the page also uses ?fig=).
export function parseInputs(params) {
  const raw = {};
  for (const s of INPUT_SPEC) {
    const v = params && typeof params.get === 'function' ? params.get(s.key) : params?.[s.key];
    if (v != null && v !== '') raw[s.key] = Number(v);
  }
  return normalize(raw);
}

export function encodeInputs(inputs, base) {
  const next = new URLSearchParams(base ? base.toString() : '');
  const n = normalize(inputs);
  for (const s of INPUT_SPEC) {
    if (n[s.key] !== DEFAULTS[s.key]) next.set(s.key, String(n[s.key]));
    else next.delete(s.key);
  }
  return next;
}

export const usdM = (v) => {
  const a = Math.abs(v);
  if (a >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${Math.round(v)}`;
};
export const pctText = (v, d = 1) => `${(v * 100).toFixed(d)}%`;
